import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useShowBar } from "@/components/Layout/MobileActionBar";
import { SimranLead } from "@/components/simran/SimranLead";
import { chatHistory, chatVisible, onSimranLecture, sectionFor, streamChat, STARTERS } from "@/lib/simranChat";
import { SIMRAN } from "@/lib/aiot";

const Dots = () => <span className="inline-flex gap-1">{[0, 1, 2].map((k) => <span key={k} className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: `${k * 0.15}s` }} />)}</span>;

const Msg = ({ m, i, page, question }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`simran-msg-${i}`}>
    <div className="max-w-[88%]">
      <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#0B1F3A] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>{m.text || <Dots />}</div>
      {m.unsure && <SimranLead question={question} page={page} i={i} />}
    </div>
  </div>
);

const useChat = (section, page, L) => {
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [suggest, setSuggest] = useState([]);
  const loaded = useRef(false);
  const load = () => { if (loaded.current) return; loaded.current = true; chatHistory().then((rows) => rows.length && setMsgs(rows.map((r) => ({ role: r.role === "user" ? "user" : "simran", text: r.text, unsure: r.unsure })))); };
  const setLast = (patch) => setMsgs((m) => [...m.slice(0, -1), { ...m[m.length - 1], ...patch }]);
  const ask = async (q) => {
    const message = q.trim();
    if (!message || busy) return;
    setBusy(true); setSuggest([]);
    setMsgs((m) => [...m, { role: "user", text: message }, { role: "simran", text: "" }]);
    try {
      await streamChat({ message, section, page }, (text) => setLast({ text }), (d) => {
        if ("unsure" in d) setLast({ unsure: d.unsure });
        if (d.suggest) setSuggest(d.suggest);
      });
    } catch (e) {
      setLast({ text: e.message === "busy" ? L("Many visitors are chatting right now. Please try again in a minute.", "अभी बहुत लोग बात कर रहे हैं। एक मिनट बाद फिर पूछिए।") : L("Sorry, I couldn't answer right now. Please try again.", "माफ़ कीजिए, अभी जवाब नहीं दे पाई। फिर से पूछिए।") });
    } finally { setBusy(false); }
  };
  return { msgs, busy, suggest, ask, load };
};

const Panel = ({ chat, section, page, onClose }) => {
  const { L } = useLang();
  const [input, setInput] = useState("");
  const box = useRef(null);
  useEffect(() => { box.current?.scrollTo({ top: 1e6, behavior: "smooth" }); }, [chat.msgs]);
  const chips = chat.suggest.length ? chat.suggest : !chat.msgs.length ? (STARTERS[section] || STARTERS.aiot).map(([en, hi]) => L(en, hi)) : [];
  return (
    <motion.div initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }} transition={{ duration: 0.2 }}
      className="w-[calc(100vw-2rem)] sm:w-[380px] h-[min(560px,calc(100vh-8rem))] rounded-3xl bg-[#F1F5F9] shadow-[0_30px_80px_-20px_rgba(11,31,58,0.55)] border border-slate-200 flex flex-col overflow-hidden" data-testid="simran-chat-panel">
      <div className="bg-[#0B1F3A] text-white px-4 py-3 flex items-center gap-3">
        <img src={SIMRAN[1]} alt="Simran Ma'am" className="w-10 h-10 rounded-full object-cover object-[50%_25%] ring-2 ring-[#14B8A6]" />
        <div className="min-w-0 flex-1"><div className="font-extrabold">Simran Ma&apos;am</div><div className="text-[11px] text-[#5EEAD4] font-semibold">{L("Admissions & support · online", "एडमिशन और सहायता · ऑनलाइन")}</div></div>
        <button type="button" onClick={onClose} aria-label="Close" data-testid="simran-chat-close" className="w-9 h-9 rounded-full grid place-items-center hover:bg-white/10"><X className="w-5 h-5" /></button>
      </div>
      <div ref={box} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="simran-chat-messages">
        <Msg m={{ role: "simran", text: L("Namaste! I'm Simran Ma'am. Ask me about courses, admissions, fees, Learn & Earn or anything on RoboSpark. Hindi, English or Hinglish, all fine!", "नमस्ते! मैं Simran Ma'am हूँ। कोर्स, एडमिशन, फ़ीस, Learn & Earn या RoboSpark के बारे में कुछ भी पूछिए। हिंदी, English या Hinglish, सब चलेगा!") }} i="greet" />
        {chat.msgs.map((m, i) => <Msg key={i} m={m} i={i} page={page} question={chat.msgs[i - 1]?.text || ""} />)}
      </div>
      {chips.length > 0 && !chat.busy && (
        <div className="px-3 pb-2 flex flex-wrap gap-1.5" data-testid="simran-chat-chips">
          {chips.map((c, i) => <button key={i} type="button" onClick={() => chat.ask(c)} data-testid={`simran-chip-${i}`} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-slate-200 text-[#0B1F3A] hover:border-[#14B8A6] hover:text-[#0F766E] transition-colors">{c}</button>)}
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); chat.ask(input); setInput(""); }} className="p-3 bg-white border-t border-slate-200 flex items-center gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} maxLength={600} placeholder={L("Type your question…", "अपना सवाल लिखिए…")} data-testid="simran-chat-input"
          className="flex-1 min-w-0 h-11 rounded-full bg-slate-100 px-4 text-sm outline-none focus:ring-2 focus:ring-[#14B8A6]/40" />
        <button type="submit" disabled={chat.busy || !input.trim()} aria-label="Send" data-testid="simran-chat-send" className="w-11 h-11 shrink-0 rounded-full grid place-items-center bg-[#14B8A6] text-[#0B1F3A] hover:bg-[#0F766E] hover:text-white disabled:opacity-40 transition-colors"><Send className="w-4 h-4" /></button>
      </form>
    </motion.div>
  );
};

export const SimranChat = () => {
  const { L } = useLang();
  const { pathname } = useLocation();
  const bar = useShowBar();
  const [open, setOpen] = useState(false);
  const [forced, setForced] = useState(null);
  const section = forced || sectionFor(pathname);
  const chat = useChat(section, pathname, L);
  const ask = useRef(chat.ask);
  ask.current = chat.ask;
  useEffect(() => {
    const on = (e) => { setOpen(true); chat.load(); if (e.detail?.section) setForced(e.detail.section); if (e.detail?.question) setTimeout(() => ask.current(e.detail.question), 50); };
    window.addEventListener("simran:open", on);
    return () => window.removeEventListener("simran:open", on);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setForced(null); }, [pathname]);
  if (!chatVisible(pathname) || (!open && !onSimranLecture(pathname))) return null;
  return (
    <div className={`fixed right-4 sm:right-6 z-50 flex flex-col items-end gap-3 ${bar ? "bottom-[4.75rem] md:bottom-3" : "bottom-3"}`} data-testid="simran-chat">
      <AnimatePresence>{open && <Panel chat={chat} section={section} page={pathname} onClose={() => setOpen(false)} />}</AnimatePresence>
      {!open && (
        <button type="button" onClick={() => { setOpen(true); chat.load(); }} data-testid="simran-chat-open"
          className="group flex items-center gap-2 rounded-full bg-[#0B1F3A] text-white pl-1.5 pr-4 py-1.5 shadow-[0_18px_40px_-12px_rgba(11,31,58,0.7)] hover:bg-[#0F766E] transition-colors">
          <span className="relative"><img src={SIMRAN[1]} alt="" className="w-10 h-10 rounded-full object-cover object-[50%_25%]" /><span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-[#0B1F3A]" /></span>
          <span className="text-sm font-extrabold hidden sm:inline">{L("Ask Simran Ma'am", "Simran Ma'am से पूछें")}</span><MessageCircle className="w-4 h-4 sm:hidden" />
        </button>
      )}
    </div>
  );
};
